// --------------------------------------------------------------
// Created On: 2026-03-20
// Author: Dimitra Weinstein
//
// Last Modified: 2026-04-14
// Modified By: Dimitra Weinstein
//
// Copyright 2026 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------
import React, { useState } from "react";
import { API } from "src/constants/miscellaneous";
import { CREATE_SUBSIDIARY_PERMISSION } from "src/constants/permissions";
import useReduxSelector from "src/hooks/useReduxSelector";
import useApi from "src/hooks/useApi";
import apiRequest from "src/utilities/api/apiRequest";
import getApiError from "src/utilities/api/getApiError";
import Spinner from "src/components/Spinner/Spinner";
import ResourceList from "src/components/ResourceList/ResourceList";
import SubsidiaryListRow from "./SubsidiaryListRow/SubsidiaryListRow";
import HelpSubsidiary from "src/components/HelpSubsidiary/HelpSubsidiary";
import CreateSubsidiaryModal from "./CreateSubsidiaryModal/CreateSubsidiaryModal";
import ManageSubsidiaryModal from "./ManageSubsidiaryModal/ManageSubsidiaryModal";
import Toast from "src/components/Toast/Toast";
import Error500Page from "../Error500Page/Error500Page";
import ErrorCannotCreateSubsidiariesPage from "../ErrorCannotCreateSubsidiariesPage/ErrorCannotCreateSubsidiariesPage";

export default function ManageSubsidiariesPage(): Component {
  const [loading, setLoading] = useState<boolean>(false);
  const [failedToLoad, setFailedToLoad] = useState<boolean>(false);
  const [showReleaseModal, setShowReleaseModal] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [confirmationMessage, setConfirmationMessage] = useState<string>("");
  const [companySubsidiaryLevel, setCompanySubsidiaryLevel] = useState<number>(0);
  const [subsidiaries, setSubsidiaries] = useState<SubsidiaryCompany[]>([]);
  const [selectedId, setSelectedId] = useState<number>(-1);
  const [companyId, setCompanyId] = useState<number>(-1);
  const [companyName, setCompanyName] = useState<string>("");
  const currentUser = useReduxSelector((state) => state.currentUser);
  const userCompanyId = currentUser.isInSubsidiary ? currentUser.subsidiaryCompanyId : currentUser.companyId;

  useApi(
    () => {
      setLoading(true);
      return true;
    },
    {
      method: "GET",
      url: `${API}/company/${userCompanyId}/subsidiary`,
    },
    async (response: Response, responseBody: GetSubsidiariesResponseBody) => {
      if (response.ok && responseBody) {
        setCompanySubsidiaryLevel(responseBody.companySubsidiaryLevel);
        setSubsidiaries(responseBody.companies);
        setLoading(false);
        setFailedToLoad(false);
        setErrorMessage("");
      } else {
        setSubsidiaries([]);
        setFailedToLoad(true);
        setLoading(false);
        setErrorMessage(await getApiError(response, "Failed to load subsidiary companies."));
      }
      setLoading(false);
    },
    [JSON.stringify(subsidiaries), userCompanyId]
  );

  async function createCompany(companyName: string): Promise<void> {
    setErrorMessage("");
    setLoading(true);
    const [response, responseBody] = (await apiRequest(`${API}/company/${userCompanyId}/subsidiary`, "POST", {
      companyName: companyName.trim(),
    })) as [Response, CreateSubsidiaryResponseBody];
    if (response.ok && responseBody) {
      const newCompany: SubsidiaryCompany = {
        companyId: responseBody.companyId,
        companyName: companyName.trim(),
      };
      setSubsidiaries([...subsidiaries, newCompany]);
      setCompanyId(responseBody.companyId);
      setCompanyName(companyName.trim());
      setErrorMessage("");
      setSelectedId(-1);
      setLoading(false);
    } else {
      setErrorMessage(await getApiError(response, "Failed to create subsidiary company. Please try again."));
      setLoading(false);
    }
  }

  async function releaseCompany(subsidiaryCompanyId: number): Promise<void> {
    setLoading(true);
    const [response, responseBody] = (await apiRequest(
      `${API}/company/${userCompanyId}/subsidiary/${subsidiaryCompanyId}`,
      "PUT",
      null
    )) as [Response, ReleaseCompanyResponseBody];
    if (response.ok && responseBody) {
      const updatedSubsidiaries = subsidiaries.filter((s) => s.companyId !== subsidiaryCompanyId);
      setSubsidiaries(updatedSubsidiaries);
      setConfirmationMessage(responseBody.message);
      setErrorMessage("");
      setLoading(false);
      setShowReleaseModal(false);
      setSelectedId(-1);
    } else {
      setConfirmationMessage("");
      setErrorMessage(await getApiError(response, "Failed to release subsidiary company."));
      setLoading(false);
    }
  }

  function sortSubsidiaries(subsidiaries: SubsidiaryCompany[]): SubsidiaryCompany[] {
    return subsidiaries.sort((a, b) => {
      const companyA = a.companyName.toUpperCase();
      const companyB = b.companyName.toUpperCase();
      if (companyA < companyB) {
        return -1;
      } else if (companyA > companyB) {
        return 1;
      } else {
        return 0;
      }
    });
  }

  function exitModal(): void {
    setSelectedId(-1);
    setErrorMessage("");
    setConfirmationMessage("");
  }

  return failedToLoad ? (
    <Error500Page />
  ) : (
    <div className="p-4">
      <Spinner loading={loading} />
      <div className="row">
        <div className="col-12 mx-0">
          {companySubsidiaryLevel >= 4 ? (
            <ErrorCannotCreateSubsidiariesPage />
          ) : (
            <ResourceList
              resourceNameSingular="Subsidiary"
              resourceNamePlural="Subsidiaries"
              resourceArticle="a"
              headerButtonLabel="Create Subsidiary"
              headerButtonLabelSmall="Create"
              headerButtonUserPermissions={[[CREATE_SUBSIDIARY_PERMISSION]]}
              resourceIdKey="companyId"
              resourcePriorityKey="companyName"
              resources={sortSubsidiaries(subsidiaries)}
              resourceRow={SubsidiaryListRow}
              helpModal={<HelpSubsidiary />}
              filterKeys={["companyName"]}
              loading={loading}
              onClickHeaderButton={() => setSelectedId(0)}
              onSelect={(selectedId) => {
                setSelectedId(selectedId);
                setCompanyId(selectedId);
                const selectedCompany = subsidiaries.find((s) => s.companyId === selectedId);
                setCompanyName(selectedCompany ? selectedCompany.companyName : "");
              }}
            />
          )}

          {confirmationMessage.length > 0 && (
            <Toast
              title="Subsidiary Company Released"
              show={confirmationMessage.length > 0}
              message={confirmationMessage}
              type="success"
              onClose={() => setTimeout(() => setConfirmationMessage(""), 200)}
            />
          )}

          <ManageSubsidiaryModal
            showModal={selectedId > 0}
            showReleaseModal={showReleaseModal}
            subsidiaryCompanyId={companyId}
            subsidiaryCompanyName={companyName}
            errorMessage={errorMessage}
            onShowReleaseModal={(show) => setShowReleaseModal(show)}
            onReleaseCompany={(subsidiaryCompanyId) => releaseCompany(subsidiaryCompanyId)}
            onClose={() => exitModal()}
          />
          <CreateSubsidiaryModal
            showModal={selectedId === 0}
            errorMessage={errorMessage}
            onCreateCompany={(companyName) => createCompany(companyName)}
            onClose={() => exitModal()}
          />
        </div>
      </div>
    </div>
  );
}

interface SubsidiaryCompany {
  companyId: number;
  companyName: string;
}

interface GetSubsidiariesResponseBody {
  companySubsidiaryLevel: number;
  companies: SubsidiaryCompany[];
}

interface CreateSubsidiaryResponseBody {
  companyId: number;
  message: string;
}

interface ReleaseCompanyResponseBody {
  message: string;
}
